import { computed } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations";
import {
    useDomainWizardState,
    type DomainLookupStatus,
} from "@/composables/useDomainWizardState";
import useDomainManipulationPermissions from "@/composables/UseDomainManipulationPermissions";
import {
    MCP_CONNECT_URL,
    MCP_CHATGPT_STEP_KEYS,
    MCP_CLAUDE_STEP_KEYS,
    MCP_REQUIREMENT_KEYS,
} from "@/constants/mcpServer";

type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface IAiPageContext {
    page: string;
    title: string;
    url: string;
    data?: Record<string, unknown>;
    account?: Record<string, unknown>;
}

interface IResolvedContext {
    page: string;
    title: string;
    data?: Record<string, unknown>;
}

interface IPageAdapter {
    id: string;
    resolve: (props: Record<string, unknown>, path: string) => IResolvedContext;
}

const pick = (
    source: unknown,
    keys: string[],
): Record<string, unknown> | undefined => {
    if (typeof source !== "object" || source === null) {
        return undefined;
    }
    const record = source as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of keys) {
        if (record[key] !== undefined) {
            out[key] = record[key];
        }
    }
    return Object.keys(out).length > 0 ? out : undefined;
};

const compact = (value: Record<string, unknown>): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
        if (val !== null && val !== undefined) {
            out[key] = val;
        }
    }
    return out;
};

const decode = (segment: string | undefined): string | undefined => {
    if (segment === undefined || segment === "") {
        return undefined;
    }
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
};

// The paginated collections the app hands to Inertia come in two shapes: a bare
// array, or a { data, total_records } envelope. Both are read the same way here.
const paginatedRows = (source: unknown): unknown[] | undefined => {
    if (Array.isArray(source) === true) {
        return source as unknown[];
    }
    if (typeof source === "object" && source !== null) {
        const data = (source as Record<string, unknown>).data;
        if (Array.isArray(data) === true) {
            return data as unknown[];
        }
    }
    return undefined;
};

const paginatedTotal = (source: unknown, fallback: number): number => {
    if (typeof source === "object" && source !== null) {
        const record = source as Record<string, unknown>;
        for (const key of ["total_records", "total"]) {
            if (typeof record[key] === "number") {
                return record[key] as number;
            }
        }
    }
    return fallback;
};

// Members and invitations are people. The assistant gets how many there are and
// in which roles — never the e-mail addresses. Identities stay behind
// list_team_users, which runs server-side and is audited.
const roleBreakdown = (
    source: unknown,
): Record<string, unknown> | undefined => {
    const rows = paginatedRows(source);

    if (rows === undefined) {
        return undefined;
    }

    const byRole: Record<string, number> = {};
    for (const row of rows) {
        if (typeof row !== "object" || row === null) {
            continue;
        }
        const role = (row as Record<string, unknown>).role;
        const key = typeof role === "string" && role !== "" ? role : "unknown";
        byRole[key] = (byRole[key] ?? 0) + 1;
    }

    return compact({
        total: paginatedTotal(source, rows.length),
        byRole: Object.keys(byRole).length > 0 ? byRole : undefined,
    });
};

// The plan as the app itself decides it, in the same order the UI does: a free
// plan wins over everything, an active subscription over a running trial.
const planLabel = (p: Record<string, unknown>): string | undefined => {
    if (p.hasFreePlan === true) {
        return "free";
    }
    if (p.isSubscribed === true) {
        return p.hasScalePlan === true ? "scale" : "paid";
    }
    if (p.isTrialing === true) {
        return "trial";
    }
    if (p.isTrialExpired === true) {
        return "trial-expired";
    }
    return undefined;
};

// The account-wide facts Inertia shares with EVERY page — plan, trial, seats,
// role. They travel next to the page payload rather than inside it, so no page
// adapter has to repeat them and the byte budget of `data` cannot swallow them.
// Without this the assistant recommends an upgrade to someone who already pays,
// or another domain to an account with no free slot. The server-provided
// CURRENT CONTEXT stays authoritative wherever the two could disagree.
const accountState = (
    p: Record<string, unknown>,
): Record<string, unknown> | undefined => {
    const team = p.team as Record<string, unknown> | null | undefined;
    const role = p.team_role as Record<string, unknown> | null | undefined;
    const trialing = p.isTrialing === true;

    const state = compact({
        plan: planLabel(p),
        billingPeriod:
            p.paidYearly === true
                ? "yearly"
                : p.paidMonthly === true
                  ? "monthly"
                  : undefined,
        // Only meaningful while a trial actually runs; outside it the shared
        // props still carry stale day counters.
        trialDaysUsed: trialing ? p.usedDays : undefined,
        trialDaysTotal: trialing ? p.defaultTrialDays : undefined,
        trialExpired: p.isTrialExpired === true ? true : undefined,
        subscriptionPastDue:
            p.isCurrentSubscriptionPastDue === true ? true : undefined,
        domainsUsed: p.domainNumber,
        domainSeatsPaid: p.paidDomainNumber,
        teamName: typeof team?.name === "string" ? team.name : undefined,
        teamRole: typeof role?.key === "string" ? role.key : undefined,
    });

    return Object.keys(state).length > 0 ? state : undefined;
};

// The on-screen pass/fail headline for a report drill-down. Kept compact (totals
// + rate only, never the full event table) so the assistant knows what the user
// is looking at and can pick tool parameters; authoritative detail still comes
// from the report tools.
const reportHeadline = (reports: unknown): Record<string, unknown> => {
    if (typeof reports !== "object" || reports === null) {
        return {};
    }
    const total = (reports as Record<string, unknown>).total;
    if (typeof total !== "object" || total === null) {
        return {};
    }
    const record = total as Record<string, unknown>;
    const pass = Number(record.pass ?? 0);
    const fail = Number(record.fail ?? 0);
    const sum = Number(record.sum ?? pass + fail);

    return {
        total: { pass, fail, sum },
        complianceRate: sum > 0 ? Math.round((pass / sum) * 1000) / 10 : 0,
    };
};

const reportGeo = (geoItem: unknown): Record<string, unknown> | undefined =>
    pick(geoItem, ["city", "region", "country", "org"]);

// The active table filter (search / sort / pagination) exactly as the server
// applied it — so the assistant sees the same filtered list the user does.
const tableFilter = (table: unknown): Record<string, unknown> | undefined =>
    pick(table, [
        "q",
        "sort_by",
        "sort_dir",
        "parked",
        "subdomain_view",
        "per_page",
        "current_page",
    ]);

const ROWS_LIMIT = 15;

// A compact digest of the table rows the user actually sees: whitelisted
// fields only, capped at ROWS_LIMIT, always accompanied by completeness
// metadata (shown / total / page) so the assistant never mistakes the visible
// slice for the whole dataset.
const visibleRows = (
    source: unknown,
    mapRow: (row: unknown) => Record<string, unknown> | undefined,
    pagination?: unknown,
): Record<string, unknown> => {
    if (!Array.isArray(source) || source.length === 0) {
        return {};
    }
    const rows: Record<string, unknown>[] = [];
    for (const raw of source) {
        const mapped = mapRow(raw);
        if (mapped !== undefined && Object.keys(mapped).length > 0) {
            rows.push(mapped);
        }
        if (rows.length >= ROWS_LIMIT) {
            break;
        }
    }
    if (rows.length === 0) {
        return {};
    }
    const pageInfo =
        pick(pagination, ["total", "current_page", "last_page"]) ?? {};
    const total =
        typeof pageInfo.total === "number" ? pageInfo.total : source.length;

    return {
        rows,
        rows_meta: compact({
            shown: rows.length,
            total,
            truncated: rows.length < total,
            page: pageInfo.current_page,
            pages: pageInfo.last_page,
        }),
    };
};

const pickRow =
    (keys: string[]) =>
    (row: unknown): Record<string, unknown> | undefined =>
        pick(row, keys);

// A domain overview row (dashboard cards, reports index table): the per-domain
// report totals live in the nested report_current object, flattened here.
const domainReportRow = (row: unknown): Record<string, unknown> | undefined => {
    if (typeof row !== "object" || row === null) {
        return undefined;
    }
    const record = row as Record<string, unknown>;
    const report =
        typeof record.report_current === "object" &&
        record.report_current !== null
            ? (record.report_current as Record<string, unknown>)
            : {};

    return compact({
        domain: record.domain,
        policy: record.dmarcPolicy,
        sum: report.sum,
        pass: report.pass,
        fail: report.fail,
    });
};

const REPORT_SOURCE_ROW_KEYS = [
    "source",
    "ip",
    "sum",
    "pass",
    "fail",
    "complientPercent",
    "spf_valid",
    "spf_invalid",
    "dkim_valid",
    "dkim_invalid",
];

const dmarcPolicyOf = (reportedDomain: unknown): string | undefined => {
    if (typeof reportedDomain !== "object" || reportedDomain === null) {
        return undefined;
    }
    const policy = (reportedDomain as Record<string, unknown>).dmarcPolicy;

    return typeof policy === "string" ? policy : undefined;
};

// The MCP server page is static onboarding and passes no props, so there is
// nothing to snapshot from `props`. Instead we hand the assistant the actual
// connect instructions the page renders — from the same i18n keys — so it can
// walk a user through connecting reliably. The model cannot open URLs, so the
// concrete steps must live in the payload text, not behind a docs link.
const buildMcpServerContext = (t: Translate): Record<string, unknown> => ({
    connectUrl: MCP_CONNECT_URL,
    requirements: {
        chatgpt: t(MCP_REQUIREMENT_KEYS.chatgpt),
        claude: t(MCP_REQUIREMENT_KEYS.claude),
    },
    chatgptSteps: MCP_CHATGPT_STEP_KEYS.map((key) => t(key)),
    claudeSteps: MCP_CLAUDE_STEP_KEYS.map((key) => t(key)),
});

// Resolves the report detail family (domain / source / ip / dmarc-management)
// from the URL segments so the assistant knows exactly what the user is viewing.
const resolveReportDetail = (
    props: Record<string, unknown>,
    path: string,
): IResolvedContext => {
    const segments = path.split("/").filter((s) => s !== "");
    const domain = decode(segments[1]);
    const kind = segments[2];
    const shared = {
        domain,
        dateRange: props.dateRange,
        table: tableFilter(props.table),
    };
    const headline = reportHeadline(props.reports);

    if (kind === "ip") {
        return {
            page: "reports.ip",
            title: "Report detail – IP",
            data: {
                ...shared,
                source: decode(segments[3]),
                ip: decode(segments[4]),
                ...headline,
                geo: reportGeo(props.geoItem),
                ...visibleRows(
                    props.tableRows,
                    pickRow(REPORT_SOURCE_ROW_KEYS),
                    props.pagination,
                ),
            },
        };
    }
    if (kind === "source") {
        return {
            page: "reports.source",
            title: "Report detail – source",
            data: {
                ...shared,
                source: decode(segments[3]),
                ...headline,
                ...visibleRows(
                    props.tableRows,
                    pickRow(REPORT_SOURCE_ROW_KEYS),
                    props.pagination,
                ),
            },
        };
    }
    if (kind === "dmarc-management") {
        return {
            page: "reports.dmarc-management",
            title: "DMARC management",
            data: { domain },
        };
    }
    // Per-domain report settings renders Reports/setting, which shares nothing
    // with the report detail below it. Without this branch it fell through and
    // the assistant was told the user is reading a report.
    if (kind === "settings") {
        return {
            page: "reports.settings",
            title: "Report settings for a domain",
            data: {
                domain,
                dmarcPolicy: dmarcPolicyOf(props.reportedDomain),
                // Which DNS provider hosts the domain decides whether the record
                // can be applied in one click or has to be published by hand.
                ...(pick(props.domainInfo, [
                    "dnsProvider",
                    "domainUsagePurpose",
                ]) ?? {}),
                isSubdomainRecord: props.isSubdomainRecord,
                parentDomainExistInDmarceye: props.parentDomainExistInDmarceye,
                unparkedDomainsCount: props.unparkedDomainsCount,
                emptyDomainsSlots: props.emptyDomainsSlots,
            },
        };
    }
    return {
        page: "reports.show",
        title: "Report detail",
        data: {
            ...shared,
            statistics: props.statistics,
            dmarcPolicy: dmarcPolicyOf(props.reportedDomain),
            ...visibleRows(
                props.tableRows,
                pickRow(REPORT_SOURCE_ROW_KEYS),
                props.pagination,
            ),
        },
    };
};

// The wizard's own vocabulary translated into what the assistant needs: whether
// the domain in the input has been looked up yet decides which control the user
// is supposed to press next.
const WIZARD_CHECK_STATUS: Record<DomainLookupStatus, string> = {
    idle: "not-checked",
    checking: "checking",
    available: "available",
    exists: "already-in-account",
    error: "check-failed",
};

// The wizard keeps its progress in its own state module and knows nothing about
// the assistant; this adapter is the AI side reading that state and shaping it,
// the same way every other page adapter shapes its props.
const resolveDomainSetupWizard = (
    props: Record<string, unknown>,
): IResolvedContext => {
    const wizard = useDomainWizardState();
    const { canManipulateDomains } = useDomainManipulationPermissions();

    const selected = pick(props.selectedDomain, [
        "domain",
        "active",
        "dmarcRecord",
    ]);
    const requested =
        typeof props.requestedDomain === "string" &&
        props.requestedDomain !== ""
            ? props.requestedDomain
            : undefined;

    const step = wizard.currentStep.value;
    const typedDomain = wizard.domain.value;
    const dnsChecked = wizard.dnsChecked.value;

    return {
        page: "domain-setup-wizard",
        title: "Domain setup wizard",
        data: {
            domain:
                typedDomain !== ""
                    ? typedDomain
                    : (selected?.domain ?? requested),
            step,
            stage: wizard.phase.value,
            domainCheckStatus:
                WIZARD_CHECK_STATUS[wizard.domainCheckStatus.value],
            domainInAccount: wizard.existingDomain.value !== null,
            // Absence of a record is only a fact once a lookup has run.
            dnsChecked,
            domainActive: wizard.dnsValidated.value,
            lastKnownDmarcRecord: dnsChecked
                ? wizard.txtRecordFromServer.value
                : undefined,
            // The record configurator only exists from step 2 onwards; quoting
            // its default on step 1 made the assistant answer about a record
            // the user cannot see yet.
            suggestedDmarcRecord:
                step > 1 ? wizard.txtRecordPreview.value : undefined,
            canAddDomains: canManipulateDomains.value,
            domainsCount: props.domainsCount,
            emptySlots: props.emptySlots,
            // Cloudflare is only detected for a domain already in the account;
            // for a new one the prop is a hardcoded false, which the assistant
            // reported as "this domain is not hosted on Cloudflare".
            isCloudflare:
                selected !== undefined ? props.isCloudflare : undefined,
        },
    };
};

// Ordered by specificity: report detail (with a domain segment) is matched
// before the generic "/reports" index.
const ADAPTERS: IPageAdapter[] = [
    {
        id: "/dashboard",
        resolve: (p) => ({
            page: "dashboard",
            title: "Dashboard",
            data: {
                dateRange: p.dateRange,
                statistics: p.statistics,
                ...visibleRows(p.activeDomains, domainReportRow),
            },
        }),
    },
    // Ahead of the "/reports/" prefix: this page has no domain segment, and the
    // detail resolver used to read "notifications" as a domain name.
    {
        id: "/reports/notifications",
        resolve: (p) => ({
            page: "reports.notifications",
            title: "Report notifications",
            data: {
                totalDomains: p.totalDomains,
                // The recipients of each notification are e-mail addresses of
                // real people and stay out of the payload.
                ...visibleRows(
                    p.notifications,
                    pickRow([
                        "name",
                        "frequency",
                        "is_active",
                        "last_sent_at",
                        "next_send_at",
                    ]),
                ),
            },
        }),
    },
    {
        id: "/reports/",
        resolve: resolveReportDetail,
    },
    {
        id: "/reports",
        resolve: (p) => ({
            page: "reports",
            title: "Reports",
            data: {
                dateRange: p.dateRange,
                table: tableFilter(p.table),
                statistics: p.statistics,
                ...visibleRows(p.activeDomains, domainReportRow, p.pagination),
            },
        }),
    },
    {
        id: "/domains",
        resolve: (p) => ({
            page: "domains",
            title: "Domains",
            data: {
                ...(pick(p, [
                    "totalDomainsCount",
                    "totalActiveDomains",
                    "totalInactiveDomains",
                    "totalParkedDomainsCount",
                    "totalEmailsReported",
                    "pagination",
                ]) ?? {}),
                table: tableFilter(p.table),
                ...visibleRows(
                    p.domains,
                    pickRow([
                        "domain",
                        "active",
                        "parked",
                        "dmarcPolicy",
                        "isSubdomain",
                        "subdomainsCount",
                    ]),
                    p.pagination,
                ),
            },
        }),
    },
    {
        id: "/blacklists",
        resolve: (p) => ({
            page: "blacklists",
            title: "Blacklists",
            data: {
                ipsUsedCount: p.ipsUsedCount,
                ipsBlacklistedCount: p.ipsBlacklistedCount,
                table: p.table,
            },
        }),
    },
    {
        id: "/alerts/overview",
        resolve: (p) => ({
            page: "alerts",
            title: "Alerts overview",
            data: {
                notificationsEnabled: p.notificationsEnabled,
                table: p.table,
                pagination: p.pagination,
            },
        }),
    },
    {
        id: "/alerts/settings",
        resolve: (p) => ({
            page: "alert-settings",
            title: "Alert settings",
            data: {
                // A team that never saved the form has no row at all, which is a
                // different answer than "alerts are switched off".
                configured: p.alert !== null && p.alert !== undefined,
                ...(pick(p.alert, [
                    "enabled",
                    "compliance_drop",
                    "time_frame",
                    "last_sent_at",
                ]) ?? {}),
            },
        }),
    },
    {
        id: "/domain-setup-wizard",
        resolve: resolveDomainSetupWizard,
    },
    {
        id: "/dns-tools",
        resolve: () => ({ page: "dns-tools", title: "DNS tools" }),
    },
    {
        id: "/integrations/mcp-server",
        resolve: () => ({ page: "mcp-server", title: "MCP server settings" }),
    },
    {
        id: "/integrations",
        resolve: (p) => ({
            page: "integrations",
            title: "Integrations",
            data: {
                // Only what identifies a connection. The `data` column of an
                // integration holds webhook URLs and access tokens.
                ...visibleRows(p.integrations, pickRow(["type", "name"])),
                filter: p.type,
            },
        }),
    },
    {
        id: "/changelog",
        resolve: (p) => ({
            page: "changelog",
            title: "Changelog",
            data: {
                // Versions and dates only; the release notes themselves would
                // eat the whole byte budget and the model cannot act on them.
                ...visibleRows(p.entries, pickRow(["version", "release_date"])),
            },
        }),
    },
    {
        id: "/support",
        resolve: () => ({ page: "support", title: "Support" }),
    },
    {
        id: "/teams",
        resolve: (p) => {
            const rows = paginatedRows(p.teams);
            const invitations =
                rows?.filter(
                    (row) =>
                        typeof row === "object" &&
                        row !== null &&
                        (row as Record<string, unknown>).isInvitation === true,
                ).length ?? 0;

            return {
                page: "teams",
                title: "Teams",
                data: {
                    teamsCount:
                        rows === undefined
                            ? undefined
                            : rows.length - invitations,
                    pendingInvitations:
                        rows === undefined ? undefined : invitations,
                    ...visibleRows(
                        rows,
                        pickRow([
                            "name",
                            "isInvitation",
                            "domains_quantity",
                            "subscription_level",
                        ]),
                    ),
                },
            };
        },
    },
    // Account, billing and member pages render almost nothing of their own into
    // props — most of what they show comes from the shared Inertia props that
    // now travel in `account`. What each adapter adds here is the handful of
    // settings that only that screen knows.
    {
        id: "/settings/profile",
        resolve: (p) => {
            const user = p.user as Record<string, unknown> | null | undefined;

            return {
                page: "profile-settings",
                title: "My Profile",
                data: {
                    language: user?.lang,
                    emailVerified: user?.email_verified_at != null,
                },
            };
        },
    },
    {
        id: "/settings/mailing-preferences",
        resolve: () => ({
            // The preference toggles are fetched with axios after mount and
            // never reach props, so there is nothing truthful to snapshot: an
            // empty payload here would read as "everything is switched off".
            page: "mailing-preferences",
            title: "Mailing preferences",
        }),
    },
    {
        id: "/settings/security",
        resolve: (p) => {
            const user = p.user as Record<string, unknown> | null | undefined;

            return {
                page: "security-settings",
                title: "Password and security",
                data: {
                    twoFactorEnabled: user?.two_factor_enabled,
                    lastLoginAt: user?.last_login_at,
                },
            };
        },
    },
    {
        id: "/settings/team/api-tokens",
        resolve: (p) => ({
            page: "api-tokens",
            title: "API tokens",
            // Whether a pairing exists and until when — never the token that
            // makes it work. The server strips credential-shaped keys anyway,
            // which is why the expiry is not named "...TokenExpiresAt" here.
            data: {
                ecomailPaired: p.ecomailPairedAt != null,
                ecomailPartner: p.ecomailPartner,
                ecomailAccessExpiresAt: p.ecomailAccessTokenExpiresAt,
            },
        }),
    },
    {
        id: "/settings/team",
        resolve: (p) => {
            const team = p.team as Record<string, unknown> | null | undefined;

            return {
                page: "account-settings",
                title: "Account settings",
                // The account ID is shown on this screen, so users ask about it.
                data: {
                    accountId: team?.id,
                    personalTeam: team?.personal_team,
                },
            };
        },
    },
    {
        id: "/billing/subscriptions",
        resolve: (p) => ({
            page: "billing",
            title: "Billing and subscription",
            data: {
                ...(pick(p, [
                    "billingTabKey",
                    "domainsCount",
                    "paidDomainsCount",
                    "emptyDomainsSlots",
                    "localPaddleDomainsQuantity",
                    "canManageDomainSeatsBilling",
                    "isOwner",
                    "trialEndsAt",
                ]) ?? {}),
            },
        }),
    },
    {
        id: "/members/invitations",
        resolve: (p) => ({
            page: "invitations",
            title: "Invitations",
            data: { pending: roleBreakdown(p.invitations) },
        }),
    },
    {
        id: "/members",
        resolve: (p) => ({
            page: "members",
            title: "Members",
            data: { members: roleBreakdown(p.members) },
        }),
    },
];

export function useAiPageContext() {
    const page = usePage();
    const { t } = useTranslations();

    const path = computed(() => page.url.split("?")[0]);

    // Demo mode renders fixture data into page props; demo data and AI are
    // strictly separate, so no page context is offered at all while it is on.
    const adapter = computed(() =>
        page.props.demoMode === true
            ? null
            : (ADAPTERS.find((a) => path.value.startsWith(a.id)) ?? null),
    );

    const available = computed(() => adapter.value !== null);

    const build = (): IAiPageContext | null => {
        if (adapter.value === null) {
            return null;
        }
        const props = page.props as Record<string, unknown>;
        const resolved = adapter.value.resolve(props, path.value);
        const data =
            resolved.page === "mcp-server"
                ? buildMcpServerContext(t)
                : compact(resolved.data ?? {});
        return {
            page: resolved.page,
            title: resolved.title,
            url: page.url,
            data: Object.keys(data).length > 0 ? data : undefined,
            account: accountState(props),
        };
    };

    const humanTitle = computed(() =>
        adapter.value === null
            ? ""
            : adapter.value.resolve(
                  page.props as Record<string, unknown>,
                  path.value,
              ).title,
    );

    return { available, humanTitle, build };
}
